import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";
import {
  ArrowDown,
  ArrowUpRight,
  Camera,
  Check,
  Clapperboard,
  Instagram,
  Mail,
  Menu,
  MessageCircle,
  Play,
  Send,
  Sparkles,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const instagramUrl = "https://www.instagram.com/rajputflix/";
const whatsappUrl = "https://wa.me/919103803049?text=Hi%20Rajputflix%2C%20I%27d%20love%20to%20discuss%20a%20creative%20project.";
const contactEmail = "shaurya@rajputflix.com";
const showreelUrl = `${instagramUrl}reel/DPbsbR7k8Qh/`;

const clients = ["Chal Chitra Talkies", "Ground Zero", "The Superhuman Project", "Digital Mahila", "Ritesh Bhandari Fit", "Ayush Mathur", "Marketing Inventors", "Power House Media"];

const testimonial = {
  quote: "Shaurya has helped me in every aspect he can with his skills and edits which helped me leverage time and energy. Exceptional edits with a lot of ideas and insights — anyone who wants to work with him will benefit greatly.",
  author: "Ritesh Bhandari Fit",
  role: "Fitness creator",
};

const heroImage = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&q=85";

const projects = [
  {
    title: "Reel Impact",
    client: "Global Clients",
    type: "Motion / Graphics",
    year: "2025",
    video: "/media/projects/DRKFn6OE9pX.mp4",
    image: "/media/projects/DRKFn6OE9pX.jpg",
    reelUrl: "https://www.instagram.com/reel/DRKFn6OE9pX/",
    number: "01",
    size: "project-large",
    accent: "Editing that travels the world.",
  },
  {
    title: "Agency, Unmasked",
    client: "Agency Truth",
    type: "Typography / Story",
    year: "2025",
    video: "/media/projects/DQtj9gvgbLC.mp4",
    image: "/media/projects/DQtj9gvgbLC.jpg",
    reelUrl: "https://www.instagram.com/reel/DQtj9gvgbLC/",
    number: "02",
    size: "project-tall",
    accent: "No smoke. No mirrors.",
  },
  {
    title: "Next Viral Video",
    client: "Growth Series",
    type: "Motion / VFX",
    year: "2025",
    video: "/media/projects/DQo2FP7gSd8.mp4",
    image: "/media/projects/DQo2FP7gSd8.jpg",
    reelUrl: "https://www.instagram.com/reel/DQo2FP7gSd8/",
    number: "03",
    size: "project-wide",
    accent: "Ideas, ignited on screen.",
  },
  {
    title: "Style Unleashed",
    client: "Clothing Brand",
    type: "Fashion / Edit",
    year: "2025",
    video: "/media/projects/DNA80u8zdn8.mp4",
    image: "/media/projects/DNA80u8zdn8.jpg",
    reelUrl: "https://www.instagram.com/reel/DNA80u8zdn8/",
    number: "04",
    size: "project-square",
    accent: "Fast cuts. Sharper fits.",
  },
  {
    title: "Scroll Stopper",
    client: "Creator Series",
    type: "Short-form / Edit",
    year: "2025",
    video: "/media/projects/DNAS7J1TMmc.mp4",
    image: "/media/projects/DNAS7J1TMmc.jpg",
    reelUrl: "https://www.instagram.com/reel/DNAS7J1TMmc/",
    number: "05",
    size: "project-xwide",
    accent: "Made to stop the feed.",
  },
  {
    title: "Crimson Vision",
    client: "Rajputflix Originals",
    type: "Showreel / VFX",
    year: "2025",
    video: "/media/projects/DFSv7xVzmln.mp4",
    image: "/media/projects/DFSv7xVzmln.jpg",
    reelUrl: "https://www.instagram.com/reel/DFSv7xVzmln/",
    number: "06",
    size: "project-end",
    accent: "Dark, red, unforgettable.",
  },
];

const services = [
  { number: "01", title: "Films that linger", text: "Branded films, music visuals, and narratives built to stay with people.", icon: Clapperboard },
  { number: "02", title: "Visual worlds", text: "Art direction and cinematography that make a brand feel unmistakably itself.", icon: Camera },
  { number: "03", title: "Motion with meaning", text: "Short-form stories, edits, and campaigns made for the way culture moves now.", icon: Sparkles },
];

function CursorGlow() {
  const glowRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return undefined;
    let raf = 0;
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let x = targetX;
    let y = targetY;
    const onMove = (event) => {
      targetX = event.clientX;
      targetY = event.clientY;
      if (glowRef.current) glowRef.current.style.opacity = "1";
    };
    const tick = () => {
      x += (targetX - x) * 0.12;
      y += (targetY - y) * 0.12;
      if (glowRef.current) glowRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <div ref={glowRef} className="cursor-glow" aria-hidden="true" data-testid="cursor-glow" />;
}

function Nav({ menuOpen, setMenuOpen }) {
  const closeMenu = () => setMenuOpen(false);
  return (
    <header className="site-nav" data-testid="site-navigation">
      <a className="brand-mark" href="#top" data-testid="brand-home-link" onClick={closeMenu}>
        <span className="brand-dot" />
        <span>RAJPUTFLIX</span>
      </a>
      <nav className={menuOpen ? "nav-links nav-links-open" : "nav-links"} data-testid="primary-navigation">
        <a href="#work" data-testid="nav-work-link" onClick={closeMenu}>Work</a>
        <a href="#services" data-testid="nav-services-link" onClick={closeMenu}>Services</a>
        <a href="#about" data-testid="nav-about-link" onClick={closeMenu}>About</a>
        <a className="nav-contact" href="#contact" data-testid="nav-contact-link" onClick={closeMenu}>Start a project <ArrowUpRight size={15} /></a>
      </nav>
      <button className="menu-trigger" type="button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation" data-testid="mobile-menu-toggle">
        {menuOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
    </header>
  );
}

function ProjectCard({ project, onSelect }) {
  const videoRef = useRef(null);
  const preview = () => videoRef.current?.play().catch(() => {});
  const stopPreview = () => {
    const video = videoRef.current;
    if (video) { video.pause(); video.currentTime = 0; }
  };
  return (
    <button className={`project-card ${project.size}`} type="button" onClick={() => onSelect(project)} onMouseEnter={preview} onMouseLeave={stopPreview} onFocus={preview} onBlur={stopPreview} data-testid={`project-card-${project.number}`}>
      <video ref={videoRef} src={project.video} poster={project.image} muted loop playsInline preload="none" aria-label={`${project.title} reel preview`} data-testid={`project-video-${project.number}`} />
      <span className="project-wash" />
      <span className="project-meta"><span>{project.number} / {project.type}</span><ArrowUpRight size={18} /></span>
      <span className="project-copy"><small data-testid={`project-client-${project.number}`}>{project.client} · {project.year}</small><strong data-testid={`project-title-${project.number}`}>{project.title}</strong></span>
    </button>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const update = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const response = await fetch(`${API}/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!response.ok) throw new Error("Request failed");
      setForm({ name: "", email: "", company: "", message: "" });
      toast.success("Brief received", { description: "We’ll be in touch soon." });
    } catch (error) {
      toast.error("Couldn’t send your brief", { description: "Please try again or reach out on Instagram." });
    } finally {
      setSending(false);
    }
  };
  return (
    <form className="contact-form" onSubmit={submit} data-testid="contact-form">
      <div className="form-row"><label htmlFor="name">Your name</label><Input id="name" name="name" value={form.name} onChange={update} placeholder="How should we call you?" required data-testid="contact-name-input" /></div>
      <div className="form-row"><label htmlFor="email">Email address</label><Input id="email" name="email" type="email" value={form.email} onChange={update} placeholder="you@studio.com" required data-testid="contact-email-input" /></div>
      <div className="form-row"><label htmlFor="company">Brand / company <em>optional</em></label><Input id="company" name="company" value={form.company} onChange={update} placeholder="Who are we creating for?" data-testid="contact-company-input" /></div>
      <div className="form-row"><label htmlFor="message">Tell us about the idea</label><Textarea id="message" name="message" value={form.message} onChange={update} placeholder="A few lines about the story, the feeling, the deadline…" required data-testid="contact-message-input" /></div>
      <Button className="submit-button" type="submit" disabled={sending} data-testid="contact-submit-button">{sending ? "Sending…" : "Send the brief"}<Send size={17} /></Button>
    </form>
  );
}

function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showreelOpen, setShowreelOpen] = useState(false);
  const modalRef = useRef(null);
  const modalCloseRef = useRef(null);

  useEffect(() => {
    if (!showreelOpen) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setShowreelOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showreelOpen]);

  useEffect(() => {
    if (!selectedProject) return undefined;
    const handleModalKeys = (event) => {
      if (event.key === "Escape") {
        setSelectedProject(null);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = modalRef.current?.querySelectorAll("button, a[href]");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleModalKeys);
    modalCloseRef.current?.focus();
    return () => document.removeEventListener("keydown", handleModalKeys);
  }, [selectedProject]);

  return (
    <main id="top" data-testid="rajputflix-portfolio">
      <div className="dynamic-backdrop" aria-hidden="true" data-testid="dynamic-glass-background">
        <span className="glass-orb glass-orb-one" />
        <span className="glass-orb glass-orb-two" />
        <span className="glass-orb glass-orb-three" />
        <span className="glass-orb glass-orb-four" />
        <span className="glass-grain" />
      </div>
      <CursorGlow />
      <Nav menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <section className="hero-section page-pad" data-testid="hero-section">
        <div className="hero-copy">
          <p className="eyebrow"><span className="live-dot" /> Independent creative media agency</p>
          <h1 data-testid="hero-heading">Stories<br /><span>with a pulse.</span></h1>
          <p className="hero-intro" data-testid="hero-intro">Rajputflix is a creative media agency for brands that refuse to blend in. We turn ideas into films, frames, and feeling.</p>
          <div className="hero-actions"><a className="red-button" href="#work" data-testid="hero-work-button">See our work <ArrowDown size={17} /></a><a className="text-link" href={instagramUrl} target="_blank" rel="noreferrer" data-testid="hero-instagram-link"><Instagram size={17} /> @rajputflix</a></div>
        </div>
        <div className="hero-visual" data-testid="hero-visual">
          <div className="hero-image-wrap"><img src={projects[0].image} alt="Rajputflix cinematic portrait direction" data-testid="hero-image" /><div className="hero-image-overlay" /><button className="hero-play" type="button" onClick={() => setShowreelOpen(true)} aria-label="Play the Rajputflix showreel" data-testid="hero-play-showreel"><Play size={20} fill="currentColor" /><span>Showreel</span></button><span className="hero-caption">A frame is a<br /><b>point of view.</b></span><span className="hero-index">[ 01 — 04 ]</span></div>
          <div className="hero-side-note">Based in India<br />Creating everywhere</div>
        </div>
        <div className="scroll-cue"><span>Scroll to explore</span><ArrowDown size={15} /></div>
      </section>

      <section className="marquee-band" aria-label="Rajputflix creative disciplines" data-testid="discipline-marquee"><div>FILM <i>✦</i> MOTION <i>✦</i> STORY <i>✦</i> CULTURE <i>✦</i> FILM <i>✦</i> MOTION <i>✦</i> STORY <i>✦</i> CULTURE <i>✦</i></div></section>

      <section className="clients-band page-pad" data-testid="clients-section">
        <p className="clients-label">Working with brands &amp; creators like</p>
        <div className="clients-row" data-testid="clients-row">
          {clients.map((name) => <span className="client-name" key={name} data-testid={`client-name-${name.toLowerCase().replace(/ /g, "-")}`}>{name}</span>)}
          <span className="client-stat" data-testid="clients-stat">30+ clients empowered</span>
        </div>
      </section>

      <section className="work-section page-pad" id="work" data-testid="work-section">
        <div className="section-heading"><div><p className="eyebrow">Selected work / 01</p><h2>Made to be <em>felt.</em></h2></div><p className="section-note">A curated edit from the Rajputflix universe.<br />More on <a href={instagramUrl} target="_blank" rel="noreferrer" data-testid="work-instagram-link">Instagram <ArrowUpRight size={13} /></a></p></div>
        <div className="projects-grid" data-testid="projects-grid">{projects.map((project) => <ProjectCard key={project.number} project={project} onSelect={setSelectedProject} />)}</div>
      </section>

      <section className="services-section page-pad" id="services" data-testid="services-section">
        <div className="section-heading"><div><p className="eyebrow">What we do / 02</p><h2>Built for the<br /><em>big feeling.</em></h2></div><p className="section-note">One studio. Every part of<br />the visual conversation.</p></div>
        <div className="service-list">{services.map(({ number, title, text, icon: Icon }) => <article className="service-item" key={number} data-testid={`service-item-${number}`}><span className="service-number">{number}</span><Icon size={27} strokeWidth={1.3} /><div><h3>{title}</h3><p>{text}</p></div><ArrowUpRight className="service-arrow" size={23} /></article>)}</div>
      </section>

      <section className="testimonial-section page-pad" data-testid="testimonial-section">
        <p className="eyebrow"><span className="live-dot" /> Client love</p>
        <blockquote data-testid="testimonial-quote">“{testimonial.quote}”</blockquote>
        <cite data-testid="testimonial-author">{testimonial.author} — {testimonial.role}</cite>
      </section>

      <section className="about-section page-pad" id="about" data-testid="about-section"><div className="about-mark">R<span>F</span></div><div className="about-copy"><p className="eyebrow">The studio / 03</p><h2>Not just content.<br /><em>A point of view.</em></h2><p>We’re a tight-knit creative crew obsessed with the details that make a story land — the light, the cut, the pause before the moment. From first thought to final frame, Rajputflix makes work with a little more soul.</p><a className="text-link" href={instagramUrl} target="_blank" rel="noreferrer" data-testid="about-instagram-link">Meet us on Instagram <ArrowUpRight size={16} /></a></div><div className="about-stats"><div><strong>30+</strong><span>clients empowered</span></div><div><strong>∞</strong><span>ways to tell it</span></div><div><strong>01</strong><span>reason to remember</span></div></div></section>

      <section className="contact-section page-pad" id="contact" data-testid="contact-section"><div className="contact-intro"><p className="eyebrow">Start something / 04</p><h2>Have a story<br /><em>in mind?</em></h2><p>Bring us the rough idea, the half-formed thought, the impossible deadline. We’ll bring the spark.</p><div className="direct-links"><a href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="whatsapp-contact-link"><MessageCircle size={18} /> WhatsApp <ArrowUpRight size={14} /></a><a href={instagramUrl} target="_blank" rel="noreferrer" data-testid="instagram-contact-link"><Instagram size={18} /> Instagram DM <ArrowUpRight size={14} /></a><a href="mailto:shaurya@rajputflix.com" data-testid="email-contact-link"><Mail size={18} /> {contactEmail} <ArrowUpRight size={14} /></a></div></div><ContactForm /></section>

      <footer className="site-footer page-pad" data-testid="site-footer"><a className="brand-mark" href="#top" data-testid="footer-brand-link"><span className="brand-dot" /><span>RAJPUTFLIX</span></a><p>© 2024 Rajputflix. Made with intent.</p><a href={instagramUrl} target="_blank" rel="noreferrer" data-testid="footer-instagram-link">Instagram <ArrowUpRight size={14} /></a></footer>

      {showreelOpen && <div className="modal-backdrop" role="presentation" onClick={() => setShowreelOpen(false)} data-testid="showreel-modal-backdrop"><div className="showreel-modal" role="dialog" aria-modal="true" aria-label="Rajputflix showreel" onClick={(event) => event.stopPropagation()} data-testid="showreel-modal"><button className="modal-close" type="button" onClick={() => setShowreelOpen(false)} aria-label="Close showreel" data-testid="showreel-modal-close"><X size={20} /></button><video src="/media/showreel.mp4" poster="/media/showreel-poster.jpg" controls autoPlay playsInline data-testid="showreel-video" /><p>Rajputflix showreel · <a href={showreelUrl} target="_blank" rel="noreferrer" data-testid="showreel-instagram-link">watch on Instagram</a></p></div></div>}

      {selectedProject && <div className="modal-backdrop" role="presentation" onClick={() => setSelectedProject(null)} data-testid="project-modal-backdrop"><div ref={modalRef} className="project-modal" role="dialog" aria-modal="true" aria-label={selectedProject.title} onClick={(event) => event.stopPropagation()} data-testid="project-modal"><button ref={modalCloseRef} className="modal-close" type="button" onClick={() => setSelectedProject(null)} aria-label="Close project details" data-testid="project-modal-close"><X size={20} /></button><video src={selectedProject.video} poster={selectedProject.image} controls autoPlay playsInline aria-label={`${selectedProject.title} reel`} data-testid="project-modal-video" /><div className="modal-copy"><p className="eyebrow">{selectedProject.type} / {selectedProject.year}</p><h2>{selectedProject.title}</h2><p>{selectedProject.accent} A considered visual world for {selectedProject.client}, shaped from first idea to final cut.</p><a className="red-button" href={selectedProject.reelUrl} target="_blank" rel="noreferrer" data-testid="project-modal-instagram-link">Watch on Instagram <ArrowUpRight size={16} /></a></div></div></div>}
      <a className="whatsapp-fab" href={whatsappUrl} target="_blank" rel="noreferrer" aria-label="Chat with Rajputflix on WhatsApp" data-testid="whatsapp-fab"><MessageCircle size={24} /></a>
      <Toaster position="bottom-right" />
    </main>
  );
}

function App() {
  return <Home />;
}

export default App;
